'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { IFileMeta } from './types';

export const MESSAGE_IMAGE_LAYOUT = {
  lg: 1024,
  fanNavigation: 280,
  fanShellMobilePadding: 16,
  fanCommunityMobilePadding: 32,
  fanShellDesktopPadding: 112,
  creatorPadding: 32,
  creatorNavigation: 280,
  creatorCollapsedNavigation: 80,
  moderationSidebar: 300,
  moderationGap: 24,
  feedMax: 800,
  moderationMax: 1200,
  postInset: 34,
  tileInset: 8,
  leadHeight: 400,
  lowerHeight: 200,
  detailMax: 876,
} as const;

export interface MessageImageWidth {
  /** A ratio of 1 means 100vw; maxPx caps the affine width. */
  viewportRatio: number;
  offsetPx: number;
  maxPx?: number;
}

export interface MessageImageLayoutWidths {
  default: MessageImageWidth;
  lg?: MessageImageWidth;
}

const PageImageWidthsContext = createContext<MessageImageLayoutWidths>({
  default: { viewportRatio: 1, offsetPx: 0 },
});

export const MessageImageLayoutProvider = ({
  widths,
  children,
}: {
  widths: MessageImageLayoutWidths;
  children: ReactNode;
}) => <PageImageWidthsContext.Provider value={widths}>{children}</PageImageWidthsContext.Provider>;

export const usePageImageLayoutWidths = () => useContext(PageImageWidthsContext);

export const getFanPageImageWidths = (authenticated: boolean): MessageImageLayoutWidths => ({
  default: {
    viewportRatio: 1,
    offsetPx:
      -MESSAGE_IMAGE_LAYOUT.fanCommunityMobilePadding -
      (authenticated ? MESSAGE_IMAGE_LAYOUT.fanShellMobilePadding : 0),
  },
  lg: authenticated
    ? {
        viewportRatio: 1,
        offsetPx: -MESSAGE_IMAGE_LAYOUT.fanNavigation - MESSAGE_IMAGE_LAYOUT.fanShellDesktopPadding,
      }
    : { viewportRatio: 0.8, offsetPx: 0 },
});

export const getCreatorPageImageWidths = (collapsed: boolean): MessageImageLayoutWidths => ({
  default: { viewportRatio: 1, offsetPx: -MESSAGE_IMAGE_LAYOUT.creatorPadding },
  lg: {
    viewportRatio: 1,
    offsetPx:
      -MESSAGE_IMAGE_LAYOUT.creatorPadding -
      (collapsed
        ? MESSAGE_IMAGE_LAYOUT.creatorCollapsedNavigation
        : MESSAGE_IMAGE_LAYOUT.creatorNavigation),
  },
});

const capWidth = (width: MessageImageWidth, cap: number): MessageImageWidth => ({
  ...width,
  maxPx: Math.min(width.maxPx ?? Infinity, cap),
});

const insetWidth = (width: MessageImageWidth, inset: number): MessageImageWidth => ({
  viewportRatio: width.viewportRatio,
  offsetPx: width.offsetPx - inset,
  ...(width.maxPx !== undefined ? { maxPx: width.maxPx - inset } : {}),
});

export const getCommunityFeedImageWidths = (
  page: MessageImageLayoutWidths,
  moderation: boolean,
  constrainMobile: boolean,
): MessageImageLayoutWidths => ({
  default: constrainMobile
    ? capWidth(
        page.default,
        moderation ? MESSAGE_IMAGE_LAYOUT.moderationMax : MESSAGE_IMAGE_LAYOUT.feedMax,
      )
    : page.default,
  lg: moderation
    ? insetWidth(
        capWidth(page.lg || page.default, MESSAGE_IMAGE_LAYOUT.moderationMax),
        MESSAGE_IMAGE_LAYOUT.moderationSidebar + MESSAGE_IMAGE_LAYOUT.moderationGap,
      )
    : capWidth(page.lg || page.default, MESSAGE_IMAGE_LAYOUT.feedMax),
});

export const insetMessageImageWidths = (
  widths: MessageImageLayoutWidths,
  inset: number,
): MessageImageLayoutWidths => ({
  default: insetWidth(widths.default, inset),
  ...(widths.lg ? { lg: insetWidth(widths.lg, inset) } : {}),
});

type LinearWidth = Pick<MessageImageWidth, 'viewportRatio' | 'offsetPx'>;
type WidthSegment = { from: number; width: LinearWidth };

const scaledWidthCandidates = (
  width: MessageImageWidth,
  factor: number,
  inset: number,
): LinearWidth[] => [
  { viewportRatio: width.viewportRatio * factor, offsetPx: width.offsetPx * factor - inset },
  ...(width.maxPx !== undefined
    ? [{ viewportRatio: 0, offsetPx: width.maxPx * factor - inset }]
    : []),
];

const valueAt = (line: LinearWidth, viewport: number) =>
  line.viewportRatio * viewport + line.offsetPx;
const sameLine = (a: LinearWidth, b: LinearWidth) =>
  a.viewportRatio === b.viewportRatio && a.offsetPx === b.offsetPx;

// Older WebKit rejects min() in sizes; emit analytic width intervals instead.
const widthSegments = (candidates: LinearWidth[], from: number, until: number): WidthSegment[] => {
  const floor: LinearWidth = { viewportRatio: 0, offsetPx: 1 };
  const lines = [...candidates, floor];
  const boundaries = new Set([from]);
  for (let a = 0; a < lines.length; a++) {
    for (let b = a + 1; b < lines.length; b++) {
      const slope = lines[a].viewportRatio - lines[b].viewportRatio;
      if (slope === 0) continue;
      const crossing = (lines[b].offsetPx - lines[a].offsetPx) / slope;
      if (crossing > from && crossing < until) boundaries.add(crossing);
    }
  }
  const points = Array.from(boundaries).sort((a, b) => a - b);
  const segments: WidthSegment[] = [];
  points.forEach((start, index) => {
    const end = points[index + 1] ?? until;
    const probe = Number.isFinite(end) ? start + (end - start) / 2 : start + 1;
    const smallest = candidates.reduce((best, line) =>
      valueAt(line, probe) < valueAt(best, probe) ? line : best,
    );
    const width = valueAt(smallest, probe) < 1 ? floor : smallest;
    if (!segments.length || !sameLine(segments[segments.length - 1].width, width))
      segments.push({ from: start, width });
  });
  return segments;
};

const cssLength = (width: LinearWidth) => {
  if (width.viewportRatio === 0) return `${width.offsetPx}px`;
  const viewport = `${width.viewportRatio * 100}vw`;
  if (width.offsetPx === 0) return viewport;
  return `calc(${viewport} ${width.offsetPx < 0 ? '-' : '+'} ${Math.abs(width.offsetPx)}px)`;
};

export const getWebMessageImageSizes = (
  image: IFileMeta,
  index: number,
  images: IFileMeta[],
  reserveSpace: boolean,
  widths: MessageImageLayoutWidths = { default: { viewportRatio: 1, offsetPx: 0 } },
): string => {
  const count = Math.min(images.length, 4);
  const lowerRowHasFixedVideo =
    index > 0 &&
    (images.length === 3 || images.length === 4) &&
    images.slice(1, 4).some((item) => item.mime_type?.startsWith('video/'));
  // Tailwind emits w-1/3 after w-1/2 in the existing three-image grid.
  const fraction = count === 2 ? 0.5 : count >= 3 && index > 0 ? 1 / 3 : 1;
  const aspect =
    Number.isFinite(image.width) &&
    image.width! > 0 &&
    Number.isFinite(image.height) &&
    image.height! > 0
      ? image.width! / image.height!
      : undefined;
  const candidates = (width: MessageImageWidth) => {
    const lines = scaledWidthCandidates(width, fraction, MESSAGE_IMAGE_LAYOUT.tileInset);
    if (!reserveSpace || !aspect) return lines;
    if (!lowerRowHasFixedVideo)
      lines.push(
        ...scaledWidthCandidates(
          width,
          fraction * 0.75 * aspect,
          MESSAGE_IMAGE_LAYOUT.tileInset * aspect,
        ),
      );
    if (lowerRowHasFixedVideo || count !== 2)
      lines.push({
        viewportRatio: 0,
        offsetPx:
          ((index === 0 ? MESSAGE_IMAGE_LAYOUT.leadHeight : MESSAGE_IMAGE_LAYOUT.lowerHeight) -
            MESSAGE_IMAGE_LAYOUT.tileInset) *
          aspect,
      });
    return lines;
  };
  const segments = widthSegments(
    candidates(widths.default),
    0,
    widths.lg ? MESSAGE_IMAGE_LAYOUT.lg : Infinity,
  );
  if (widths.lg) {
    for (const segment of widthSegments(candidates(widths.lg), MESSAGE_IMAGE_LAYOUT.lg, Infinity)) {
      if (!sameLine(segments[segments.length - 1].width, segment.width)) segments.push(segment);
    }
  }
  return segments
    .reverse()
    .map(
      (segment) =>
        `${segment.from > 0 ? `(min-width: ${segment.from}px) ` : ''}${cssLength(segment.width)}`,
    )
    .join(', ');
};
